// Sketch modal — file upload, analyze, create project from sketch
import { t, getDocumentLanguage } from '../shared/i18n.ts';
import { showToast, authenticatedFetch } from '../shared/utils.ts';
import { isAuthenticated, clearAuthState } from '../shared/auth.ts';
import { login_path, localized_planner_path } from '../shared/routes';
import { api_v1_projects_from_sketch_path } from '../shared/api-routes';
import { showModal, hideModal } from '../shared/modal.ts';

let sketchImageData: string | null = null;
let sketchContentType: string | null = null;

export function openSketchModal() {
  if (!showModal('sketchModal')) return;
  sketchImageData = null;
  sketchContentType = null;
  resetSketchUI();
}

export function closeSketchModal() {
  hideModal('sketchModal');
}

function resetSketchUI() {
  const dropZone = document.getElementById('sketchDropZone');
  const preview = document.getElementById('sketchPreview');
  const analyzeBtn = document.getElementById('sketchAnalyzeBtn');
  const loading = document.getElementById('sketchLoading');
  const fileInput = document.getElementById('sketchFileInput');

  if (dropZone) dropZone.hidden = false;
  if (preview) preview.hidden = true;
  if (analyzeBtn) (analyzeBtn as HTMLButtonElement).disabled = true;
  if (loading) loading.hidden = true;
  if (fileInput) (fileInput as HTMLInputElement).value = '';
}

function handleSketchFile(file: any) {
  if (!file) return;

  // Mirrors Sketches::CreateFromSketch::ALLOWED_CONTENT_TYPES. HEIC is back
  // on the list: the server transcodes every upload through libvips before it
  // reaches the model (#1610), so the format an iPhone shoots by default is
  // fine again — #1591 only had to reject it while the bytes went through
  // untouched.
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  if (!allowedTypes.includes(file.type)) {
    showToast(t('sketch.error'), 'error');
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    showToast(t('sketch.error'), 'error');
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    const base64Full = e.target!.result;
    sketchImageData = (base64Full as string).split(',')[1];
    sketchContentType = file.type;

    const dropZone = document.getElementById('sketchDropZone');
    const preview = document.getElementById('sketchPreview');
    const previewImg = document.getElementById('sketchPreviewImg');
    const analyzeBtn = document.getElementById('sketchAnalyzeBtn');
    const hadFocus = !!dropZone && dropZone.contains(document.activeElement);

    if (dropZone) dropZone.hidden = true;
    if (preview) preview.hidden = false;
    if (previewImg) (previewImg as HTMLImageElement).src = base64Full as string;
    if (analyzeBtn) (analyzeBtn as HTMLButtonElement).disabled = false;
    // Hiding the focused drop zone would drop keyboard focus onto <body>,
    // outside the modal; the preview's Remove button takes its place.
    if (hadFocus) document.getElementById('sketchRemoveImg')?.focus();
  };
  reader.readAsDataURL(file);
}

export async function analyzeSketch() {
  if (!sketchImageData || !sketchContentType) return;

  if (!isAuthenticated()) {
    window.location.href = login_path();
    return;
  }

  const loading = document.getElementById('sketchLoading');
  const analyzeBtn = document.getElementById('sketchAnalyzeBtn');
  if (loading) loading.hidden = false;
  if (analyzeBtn) (analyzeBtn as HTMLButtonElement).disabled = true;

  const nameInput = document.getElementById('sketchProjectName');
  const name = (nameInput && (nameInput as HTMLInputElement).value.trim()) || t('sketch.defaultProjectName');

  let resp;
  try {
    resp = await authenticatedFetch(api_v1_projects_from_sketch_path(), {
      method: 'POST',
      body: {
        image: sketchImageData,
        content_type: sketchContentType,
        name: name,
      },
    });
  } catch {
    if (loading) loading.hidden = true;
    if (analyzeBtn) (analyzeBtn as HTMLButtonElement).disabled = false;
    showToast(t('dashboard.networkError'));
    return;
  }

  if (loading) loading.hidden = true;

  if (resp.ok) {
    const data = await resp.json();
    closeSketchModal();
    window.location.href = `${localized_planner_path(getDocumentLanguage())}?project=${data.project.id}`;
  } else if (resp.status === 401) {
    clearAuthState();
    window.location.href = login_path();
  } else if (resp.status === 403) {
    const err = await resp.json().catch(() => ({}));
    showToast(err.details || t('sketch.tierLimit'), 'warning');
    if (analyzeBtn) (analyzeBtn as HTMLButtonElement).disabled = false;
  } else {
    const err = await resp.json().catch(() => ({}));
    showToast(err.error || err.details || t('sketch.error'));
    if (analyzeBtn) (analyzeBtn as HTMLButtonElement).disabled = false;
  }
}

export function initSketchModal() {
  const btnSketch = document.getElementById('btnSketchUpload');
  if (btnSketch) btnSketch.addEventListener('click', openSketchModal);

  const closeBtn = document.getElementById('sketchModalClose');
  if (closeBtn) closeBtn.addEventListener('click', closeSketchModal);

  const cancelBtn = document.getElementById('sketchCancelBtn');
  if (cancelBtn) cancelBtn.addEventListener('click', closeSketchModal);

  const analyzeBtn = document.getElementById('sketchAnalyzeBtn');
  if (analyzeBtn) analyzeBtn.addEventListener('click', analyzeSketch);

  const fileInput = document.getElementById('sketchFileInput');
  if (fileInput) {
    fileInput.addEventListener('change', (e) => {
      handleSketchFile((e.target as HTMLInputElement).files![0]);
    });
  }

  const dropZone = document.getElementById('sketchDropZone');
  if (dropZone) {
    const openPicker = () => document.getElementById('sketchFileInput')?.click();
    dropZone.addEventListener('click', openPicker);
    // The zone is the only way to the file input, which is `hidden` and so
    // never takes focus; it carries role="button" and answers the keys one does.
    dropZone.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openPicker();
    });

    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('drag-over');
    });

    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('drag-over');
    });

    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('drag-over');
      const file = e.dataTransfer!.files[0];
      handleSketchFile(file);
    });
  }

  const removeBtn = document.getElementById('sketchRemoveImg');
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      sketchImageData = null;
      sketchContentType = null;
      resetSketchUI();
      document.getElementById('sketchDropZone')?.focus();
    });
  }

  const modal = document.getElementById('sketchModal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeSketchModal();
    });
  }
}
